import React, { FC, useContext, useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'querystring';
import {
  FormControl,
  TextField,
  Theme,
  InputAdornment,
  Tooltip,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ArrowBack, Visibility, VisibilityOff, Password } from '@mui/icons-material';
import { Formik, FormikHelpers, Field, FieldProps, FormikProps, Form } from 'formik';
import * as Yup from 'yup';
import { Button, MinimalPage, RedirectUser } from '../../components';
// fetch
import {
  resetPassword,
  login,
  getUserMfaOptions,
  getUserMfaTypes,
  validateResetPassword
} from '../../fetch';
// helpers
import { generatePassword, passwordRegex } from '../../helpers';
import { IResetPasswordPost, IAuthUserMFAOptions, IDropdownResponse } from '../../models';
// context
import { UserContext } from '../../context';
import { MFAFlow } from '../login/mfa-flow';

const resetPasswordSchema = Yup.object().shape({
  confirm: Yup.string()
    .required('Required')
    .min(10, 'Password must be at least 10 characters')
    .matches(passwordRegex, {
      message: 'Invalid Password'
    })
    .oneOf([Yup.ref('password'), null], `Passwords don't match`),
  email: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string()
    .required('Required')
    .min(10, 'Password must be at least 10 characters')
    .matches(passwordRegex, {
      message: 'Invalid password'
    })
});

interface IResetPasswordFormValues {
  confirm: string;
  email: string;
  password: string;
}

export const ResetPassword: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const { user, isLoading, setUser } = useContext(UserContext);
  const [isShowingPassword, setIsShowingPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const history = useHistory();
  // state
  const [existingMfaOptions, setExistingMfaOptions] = useState<IAuthUserMFAOptions[] | null>(null);
  const [mfaTypes, setMFATypes] = useState<IDropdownResponse[] | null>(null);

  // Get token from URL
  const location = useLocation();

  const isCreatingPassword = location.pathname === '/create-password';
  const queryString = location.search.replace(/^\?/, '');
  const qsParsed = qs.parse(queryString);

  const email = qsParsed.email ? String(qsParsed.email) : '';
  //QS is stripping out the + in the token which is a valid character
  const tokenCode = encodeURI(qsParsed.token ? String(qsParsed.token) : '').replace(/%20/g, '+');
  const classes = useStyles();

  useEffect(() => {
    const checkToken = async () => {
      try {
        // if we get a 200 then we know the combination is valid and they are good to proceed with the reset
        await validateResetPassword({
          email,
          passwordToken: tokenCode
        });
        return;
      } catch (error) {
        // get any type of error redirect to login
        history.push('/login');
      } finally {
        setIsValidating(false);
      }
    };

    if (tokenCode && email) {
      checkToken();
    } else {
      setIsValidating(false);
    }
  }, [tokenCode, email, history]);

  if (isLoading || isValidating) {
    return null;
  }

  if (user && !isLoading) {
    return <RedirectUser />;
  }

  return (
    <MinimalPage title={isCreatingPassword ? 'Create Password' : 'Reset Password'}>
      <MFAFlow
        setUser={setUser}
        existingMfaOptions={existingMfaOptions}
        mfaTypes={mfaTypes}
        resetLogin={() => {
          setExistingMfaOptions(null);
          setMFATypes(null);
        }}
      />
      {!mfaTypes && !existingMfaOptions && (
        <Formik
          initialValues={{
            confirm: '',
            email,
            password: ''
          }}
          onSubmit={async (
            values: IResetPasswordFormValues,
            actions: FormikHelpers<IResetPasswordFormValues>
          ) => {
            setLoading(true);
            const data: IResetPasswordPost = {
              email: values.email,
              resetPasswordToken: tokenCode,
              newPassword: values.password
            };

            try {
              ///reset-password?token=O1EXxWsJn5dw7twrFP11c9ON+EQoOwRg11N+a/TybT0/LEV/Yz2j2vnOUwrUp/vh54rOT19gTNQH5GncnBNTaA==
              await resetPassword(data);
              enqueueSnackbar('Password reset, complete the login', {
                variant: 'success'
              });
              const res = await login({
                userName: values.email,
                password: values.password
              });
              const [userMfaOptions, mfaTypes] = await Promise.all([
                getUserMfaOptions({ isActive: true }),
                getUserMfaTypes()
              ]);
              setExistingMfaOptions(userMfaOptions);
              if (res.roles.includes('MFA_Setup_Required')) {
                setMFATypes(mfaTypes);
              }
            } catch (error) {
              enqueueSnackbar(error?.Detail || 'Error, resetting password, please try again.', {
                variant: 'error'
              });
            } finally {
              actions.setSubmitting(false);
              setLoading(false);
            }
          }}
          validationSchema={resetPasswordSchema}
        >
          {(formikProps: FormikProps<IResetPasswordFormValues>) => {
            return (
              <>
                <Form
                  data-testid="reset-password-form"
                  id="reset-password-form"
                  onSubmit={formikProps.handleSubmit}
                >
                  <FormControl fullWidth={true} margin="normal">
                    <Field name="email">
                      {({
                        field,
                        form: { touched, errors, setFieldValue }
                      }: FieldProps<IResetPasswordFormValues>) => (
                        <TextField
                          {...field}
                          error={touched.email && errors.email ? true : false}
                          fullWidth={true}
                          helperText={touched.email && errors.email}
                          id="reset-password-email"
                          label="Email"
                          onChange={e => setFieldValue('email', e.target.value)}
                          required={true}
                          type="text"
                        />
                      )}
                    </Field>
                  </FormControl>
                  <FormControl fullWidth={true} margin="normal">
                    <Field name="password">
                      {({
                        field,
                        form: { touched, errors, setFieldValue, setTouched }
                      }: FieldProps<IResetPasswordFormValues>) => (
                        <>
                          <TextField
                            {...field}
                            error={touched.password && errors.password ? true : false}
                            fullWidth={true}
                            helperText={touched.password && errors.password}
                            id="reset-password-password"
                            label="Password"
                            onChange={e => setFieldValue('password', e.target.value)}
                            required={true}
                            type={isShowingPassword ? 'input' : 'password'}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  className={classes.passwordIcon}
                                  onClick={() => {
                                    setIsShowingPassword(!isShowingPassword);
                                  }}
                                >
                                  {isShowingPassword ? <VisibilityOff /> : <Visibility />}
                                </InputAdornment>
                              )
                            }}
                          />
                          <Stack flexDirection="row" justifyContent="center" alignItems="center">
                            <Tooltip title="Generate Password">
                              <IconButton
                                onClick={() => {
                                  const generatedPassword = generatePassword();
                                  setFieldValue('password', generatedPassword);
                                  setFieldValue('confirm', generatedPassword);
                                  setTouched({ ...touched, password: true, confirm: true });
                                  setTimeout(() => {
                                    formikProps.validateForm();
                                  }, 200);
                                }}
                                color="primary"
                              >
                                <Password />
                              </IconButton>
                            </Tooltip>
                            <Typography variant="body2">Click to autogenerate password</Typography>
                          </Stack>
                        </>
                      )}
                    </Field>
                  </FormControl>
                  <FormControl fullWidth={true} size="small">
                    <ul className={classes.list}>
                      <li>Must be at least 10 characters long</li>
                      <li>Must have at least one numeric character</li>
                      <li>Must have at least one &quot;special&quot; character, ex: !$#@%</li>
                    </ul>
                  </FormControl>
                  <FormControl fullWidth={true} margin="normal">
                    <Field name="confirm">
                      {({
                        field,
                        form: { touched, errors, setFieldValue }
                      }: FieldProps<IResetPasswordFormValues>) => (
                        <TextField
                          {...field}
                          error={touched.confirm && errors.confirm ? true : false}
                          fullWidth={true}
                          helperText={touched.confirm && errors.confirm}
                          id="reset-password-confirm"
                          label="Confirm Password"
                          onChange={e => setFieldValue('confirm', e.target.value)}
                          required={true}
                          type={isShowingPassword ? 'input' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                className={classes.passwordIcon}
                                onClick={() => {
                                  setIsShowingPassword(!isShowingPassword);
                                }}
                              >
                                {isShowingPassword ? <VisibilityOff /> : <Visibility />}
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    </Field>
                  </FormControl>
                  <FormControl fullWidth={true} margin="normal">
                    <Button
                      color="primary"
                      disabled={!formikProps.dirty || !formikProps.isValid || loading}
                      fullWidth={true}
                      id="reset-password-button"
                      loading={loading ? 'Resetting...' : undefined}
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Save and Login
                    </Button>
                  </FormControl>
                  <FormControl fullWidth={true} margin="normal">
                    <Button
                      className={classes.back}
                      color="primary"
                      fullWidth={true}
                      id="back-to-login"
                      asLink={true}
                      to={'/login'}
                      variant="text"
                      startIcon={<ArrowBack />}
                    >
                      Back to Login
                    </Button>
                  </FormControl>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </MinimalPage>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    back: {
      textDecoration: 'none',
      textTransform: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    icon: {
      height: '15px',
      marginRight: theme.spacing(1),
      width: '15px !important'
    },
    paper: {
      maxWidth: 360
    },
    list: {
      fontSize: '0.875rem',
      margin: 0,
      padding: '0 0 0 1rem'
    },
    passwordIcon: {
      cursor: 'pointer',
      color: theme.palette.grey[500]
    }
  };
});
